/**
 * "Where I was" in the discussion panel, held by the REQUEST SCREEN rather
 * than by a panel mount (#1069, owner rulings 2026-09-25/26,
 * `spec-1069-panel-survives-resize.md`).
 *
 * ⚠️ WHY IT IS NOT PANEL STATE. On web the panel has two mounts: the docked
 * one `RequestDetail` renders in its layout row, and the overlay one
 * `CorrespondenceLauncher` renders at the root. Crossing the dock threshold
 * (1280) unmounts one and mounts the other, and a docked close unmounts the
 * panel once its collapse finishes, while an overlay close never did. So a
 * resize dropped the draft, and a close dropped it on one platform and kept
 * it on another. Ruling 2: the place belongs to the screen, survives a resize
 * AND a close, everywhere, until the user leaves the request. React Native
 * cannot reparent one instance between the two sites without a remount,
 * which is why the state moves rather than the panel.
 *
 * ⚠️ EVERYTHING THAT BELONGS WITH THE MESSAGE TRAVELS WITH IT (ruling 1).
 * Loop 1 of this spec lifted six values and the review found each thing left
 * behind was a defect: the in-flight flag (a swap mid-send re-enabled Post, a
 * double send), `mayAlreadyBeSent` (the caution dropped while its files
 * stayed), `sendError` (the "Not sent" line dropped while its text stayed),
 * and the compose pair (a new conversation's body stranded as the next
 * reply's draft). A field joins this list when losing it on a remount would
 * mislead the user about their message; dialogs, hover, scroll and the files
 * strip stay in the panel.
 *
 * ⚠️ A STORE, NOT SCREEN `useState`. The draft changes on every keystroke and
 * `RequestDetail` is a ~6000-line screen; screen state would re-render all of
 * it per character. The screen only owns the store's LIFETIME (one per
 * request, see `useCorrespondenceSessionFor`) and never subscribes; the panel
 * subscribes field by field.
 *
 * ⚠️ WRITES ARE SYNCHRONOUS AND NOTIFY AT ONCE, which is what lets
 * `writeInFlight` replace the panel's old `submittingRef`: two taps in one
 * tick read `get()` after the first one's `set`, not a render-time snapshot.
 * The uploadOverlay store is the precedent for the listener loop.
 */
import {
  useCallback,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from 'react';

import type { SpaceId } from './correspondence.types';
import type { StagedFile } from '../../services/correspondence/stagedFile';

export interface CorrespondenceSessionState {
  space: SpaceId;
  openThreadId: string | null;
  draft: string;
  /** Keyed by thread, least-recently-touched first: see the panel. */
  stagedByThread: Map<string, readonly StagedFile[]>;
  /** The thread whose strip carries the may-already-be-sent caution. */
  mayAlreadyBeSent: string | null;
  /** The composer's unsent / unconfirmed line. */
  sendError: string | null;
  composingThread: boolean;
  newTitle: string;
  editing: { messageId: string } | null;
  /** The draft an edit displaced, restored on Cancel. */
  draftBeforeEdit: string;
  /** What the composer held when the edit began: unchanged means nothing to lose. */
  editPrefill: string;
  /** Post and create in flight: drives the composer's sending state. */
  submitting: boolean;
  /**
   * Any write in flight (post, create, save, delete): the double-submit
   * guard. Wider than `submitting` because a delete guards without showing
   * the composer as sending, which is what the panel's ref always did.
   */
  writeInFlight: boolean;
  /**
   * The open-thread mark-read token (`useCorrespondence`), so a remount onto
   * an already-marked thread does not mark it again on every resize. Released
   * when the panel closes, so a reopen marks again (review, 2026-09-26).
   */
  markedToken: string | null;
  /**
   * The server's explanation for the last refused write (Story 1.3 AC 37),
   * which the unsent line shows in place of the generic copy. Session state
   * because the refusal can land in the mount a resize just retired.
   */
  writeRefusal: string | null;
  /**
   * The ids the last refused write's `MENTION_NOT_ALLOWED` named (#1599
   * Story 1.4, AC 9): the tokens the editor outlines and "Remove @Name and
   * send" strips. It travels with the message (ruling 1): losing it on a
   * remount would hide which tokens were refused while the draft still
   * carries them, and the "Not sent" sentence above would then point at
   * nothing. Cleared with `writeRefusal` by the hook's one setter, and by a
   * landed post (`settleSentPost`) and a revoke.
   */
  refusedMentionIds: readonly string[];
  /**
   * Whether the viewer may see the internal space, as the last mounted panel
   * derived it. With `space` it gives the EFFECTIVE space (`activeSpaceOf`),
   * which is what a create in flight compares at landing: an external viewer's
   * `space` can say 'internal' while everything they see is 'shared'.
   */
  canSeeInternal: boolean;
  /**
   * The device has been offline since the last reconnect ran. Session state
   * so a mount that starts ONLINE after an offline period it never saw (a
   * docked close across the drop) still clears the lines that describe the
   * connection rather than the reader's work (offline spec).
   */
  reconnectPending: boolean;
  /**
   * The open conversation's cache keys as the last mount committed them, so
   * the thread-switch removal (Story 6.4 D3) still runs when the session's
   * thread changed while NO panel was mounted (a create landing after a
   * docked close).
   */
  threadCacheKeys: {
    messages: readonly unknown[];
    files: readonly unknown[];
  } | null;
  /**
   * The conversation the arrival URL named (#1599 Story 2.3, `thread=`),
   * consumed by the panel's resolver (`useArrivalThread`). A field here
   * because the resolver's result must outlive the mount that ran it: a
   * resize mid-resolution remounts the panel, and the session survives.
   */
  arrivalThreadId: string | null;
}

type Key = keyof CorrespondenceSessionState;

export interface CorrespondenceSession {
  get(): CorrespondenceSessionState;
  set<K extends Key>(
    key: K,
    action: SetStateAction<CorrespondenceSessionState[K]>,
  ): void;
  subscribe(listener: () => void): () => void;
  /** A stable setter per field, so callers can list it as a dependency. */
  setterFor<K extends Key>(
    key: K,
  ): Dispatch<SetStateAction<CorrespondenceSessionState[K]>>;
  /**
   * A `{ current }` view of one field, for the values the panel only ever
   * read imperatively (they were refs). Reading or writing it never renders.
   */
  refFor<K extends Key>(key: K): { current: CorrespondenceSessionState[K] };
}

const initialState = (): CorrespondenceSessionState => ({
  space: 'internal',
  openThreadId: null,
  draft: '',
  stagedByThread: new Map(),
  mayAlreadyBeSent: null,
  sendError: null,
  composingThread: false,
  newTitle: '',
  editing: null,
  draftBeforeEdit: '',
  editPrefill: '',
  submitting: false,
  writeInFlight: false,
  markedToken: null,
  writeRefusal: null,
  refusedMentionIds: [],
  canSeeInternal: false,
  reconnectPending: false,
  threadCacheKeys: null,
  arrivalThreadId: null,
});

/**
 * The space the viewer is actually in: an external viewer is always in the
 * shared one, whatever `space` says. The panel's `activeSpace`, from state.
 */
export const activeSpaceOf = (
  state: Pick<CorrespondenceSessionState, 'space' | 'canSeeInternal'>,
): SpaceId =>
  state.space === 'internal' && !state.canSeeInternal ? 'shared' : state.space;

/** What one post or save sent, captured at the press. */
export interface SentPost {
  threadId: string;
  /** The composer's text at the press, untrimmed, exactly as held. */
  draftAtPress: string;
  /** The staged files that went, by identity; empty for a save. */
  sentFiles: readonly StagedFile[];
  /** The edit being saved, or `null` for a post. */
  editAtPress: { messageId: string } | null;
}

/**
 * A post or save LANDED: clear what it sent and nothing else (2026-09-25).
 *
 * ⚠️ THE COMPLETION MAY RUN IN A MOUNT THAT NO LONGER EXISTS. A resize or a
 * docked close mid-send remounts the panel, and the send's closure keeps
 * running against the session the new mount shows. So: the draft only if it
 * is still exactly the text that was sent, the strip's files by identity
 * (anything staged since stays), the edit only if it is still this edit. A
 * write that bypasses this and clears unconditionally is the regression
 * `correspondenceSession.test.ts` pins.
 */
export function settleSentPost(
  session: CorrespondenceSession,
  sent: SentPost,
): void {
  if (sent.editAtPress) {
    const messageId = sent.editAtPress.messageId;
    if (session.get().editing?.messageId === messageId) {
      session.set('editing', null);
      session.set('editPrefill', '');
      session.set('draftBeforeEdit', '');
    }
  } else {
    session.set('mayAlreadyBeSent', current =>
      current === sent.threadId ? null : current,
    );
    session.set('stagedByThread', all => {
      const current = all.get(sent.threadId);
      if (!current) return all;
      const kept = current.filter(file => !sent.sentFiles.includes(file));
      if (kept.length === current.length) return all;
      const next = new Map(all);
      if (kept.length === 0) next.delete(sent.threadId);
      else next.set(sent.threadId, kept);
      return next;
    });
  }
  session.set('draft', current =>
    current === sent.draftAtPress ? '' : current,
  );
  // #1599 Story 1.4: a write landed, so nothing it sent is refused any more.
  session.set('refusedMentionIds', current =>
    current.length === 0 ? current : [],
  );
}

/**
 * A create LANDED: leave compose mode only if the title and body still hold
 * what was sent. If the user has written something else there since, it is
 * the next message, and it stays where they left it. Returns whether it
 * cleared.
 */
export function settleSentCreate(
  session: CorrespondenceSession,
  sent: { titleAtPress: string; draftAtPress: string },
): boolean {
  const state = session.get();
  if (state.newTitle !== sent.titleAtPress || state.draft !== sent.draftAtPress)
    return false;
  session.set('composingThread', false);
  session.set('newTitle', '');
  session.set('draft', '');
  return true;
}

/**
 * Access to the request is gone (D3): nothing of the reader's place survives
 * it, because a restored access would otherwise resume an edit of a message,
 * or a compose, that belonged to the revoked view. A write still in flight
 * keeps its own flags; its `finally` releases them.
 */
export function resetSessionAfterRevoke(session: CorrespondenceSession): void {
  // Named field by field rather than looped over `initialState()`: a new
  // field has to decide whether a revoke takes it, and the in-flight flags,
  // the viewer's standing and the owed reconnect deliberately stay.
  const fresh = initialState();
  session.set('space', fresh.space);
  session.set('openThreadId', fresh.openThreadId);
  session.set('draft', fresh.draft);
  session.set('stagedByThread', () => fresh.stagedByThread);
  session.set('mayAlreadyBeSent', fresh.mayAlreadyBeSent);
  session.set('sendError', fresh.sendError);
  session.set('composingThread', fresh.composingThread);
  session.set('newTitle', fresh.newTitle);
  session.set('editing', fresh.editing);
  session.set('draftBeforeEdit', fresh.draftBeforeEdit);
  session.set('editPrefill', fresh.editPrefill);
  session.set('markedToken', fresh.markedToken);
  session.set('writeRefusal', fresh.writeRefusal);
  session.set('refusedMentionIds', fresh.refusedMentionIds);
  // #1599 Story 2.3: an arrival not yet resolved belonged to the revoked view.
  session.set('arrivalThreadId', fresh.arrivalThreadId);
  // NOT `threadCacheKeys`: it records what is in the CACHE, and the removal
  // effect needs it to drop the revoked conversation's pages.
  // (Conversations kept while offline are not session state at all: they
  // live on the query client, `keptOffline.ts`, and a revoke removes them
  // there, online or not.)
}

export function createCorrespondenceSession(
  seed?: Pick<CorrespondenceSessionState, 'arrivalThreadId'>,
): CorrespondenceSession {
  let state = seed ? { ...initialState(), ...seed } : initialState();
  const listeners = new Set<() => void>();
  // `Map`s, keyed by field name: the key is ours, never the caller's text,
  // and a `Map` keeps this file clear of the object-lookup rule anyway.
  const setters = new Map<Key, unknown>();
  const refs = new Map<Key, unknown>();

  const session: CorrespondenceSession = {
    get: () => state,
    set(key, action) {
      const previous = state[key];
      const next =
        typeof action === 'function'
          ? (action as (prev: typeof previous) => typeof previous)(previous)
          : action;
      if (Object.is(next, previous)) return;
      state = { ...state, [key]: next };
      listeners.forEach(listener => listener());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setterFor(key) {
      let setter = setters.get(key);
      if (!setter) {
        setter = (
          action: SetStateAction<CorrespondenceSessionState[typeof key]>,
        ) => session.set(key, action);
        setters.set(key, setter);
      }
      return setter as Dispatch<
        SetStateAction<CorrespondenceSessionState[typeof key]>
      >;
    },
    refFor(key) {
      let ref = refs.get(key);
      if (!ref) {
        ref = {
          get current() {
            return state[key];
          },
          set current(value: CorrespondenceSessionState[typeof key]) {
            session.set(key, () => value);
          },
        };
        refs.set(key, ref);
      }
      return ref as { current: CorrespondenceSessionState[typeof key] };
    },
  };
  return session;
}

/** One field, as `useState` would give it: the value and a stable setter. */
export function useSessionField<K extends Key>(
  session: CorrespondenceSession,
  key: K,
): [
  CorrespondenceSessionState[K],
  Dispatch<SetStateAction<CorrespondenceSessionState[K]>>,
] {
  const getSnapshot = useCallback(() => session.get()[key], [session, key]);
  const value = useSyncExternalStore(session.subscribe, getSnapshot);
  return [value, session.setterFor(key)];
}

/**
 * The screen's session: one per request AND viewer, dropped with the screen.
 *
 * ⚠️ STATE, ADJUSTED DURING RENDER, NOT `useMemo`. React documents `useMemo`
 * as a cache it may discard, and a discarded session is a lost draft. A
 * request change mints a fresh store in the same render, so no mount ever
 * reads one request's conversation under another request (ruling 2's
 * "until you leave the request"). Nothing else resets it: closing the panel
 * never does.
 *
 * ⚠️ AND THE VIEWER IS PART OF THE KEY (review, 2026-09-26). The session
 * outlives a close, and impersonation starts and stops with this screen
 * mounted: keyed on the request alone, the next identity would find the
 * previous one's draft, staged files and open conversation, one Post away
 * from sending them as somebody else.
 *
 * `arrivalThreadId` (#1599 Story 2.3) seeds every store minted for the SAME
 * request, so the re-mint when `user?.id` first resolves keeps the arrival.
 * A request change mints an unseeded store: the arrival belonged to the
 * request the URL named (the screen passes `null` then anyway).
 */
export function useCorrespondenceSessionFor(
  requestId: string | undefined,
  viewerId: string | undefined,
  arrivalThreadId: string | null = null,
): CorrespondenceSession {
  const [held, setHeld] = useState(() => ({
    requestId,
    viewerId,
    session: createCorrespondenceSession({ arrivalThreadId }),
  }));
  if (held.requestId !== requestId || held.viewerId !== viewerId) {
    const next = {
      requestId,
      viewerId,
      session: createCorrespondenceSession({
        arrivalThreadId: held.requestId === requestId ? arrivalThreadId : null,
      }),
    };
    setHeld(next);
    return next.session;
  }
  return held.session;
}
